import { Fragment, useEffect, useState } from 'react'
import AdminLayout from '../../components/shell/AdminLayout'
import EmptyState from '../../components/shell/EmptyState'
import AuditEventDetails from '../../components/admin/AuditEventDetails'
import { AUDIT_CATEGORIES, type AuditLookups } from './auditEvents'
import { AUDIT_PAGE_SIZE, NO_FILTERS, getAuditLog, getAuditLookups, type AuditFilters, type AuditLog } from './auditLogStore'
import { formatClockTimesInText, useTimeFormat, type TimeFormat } from '../../lib/timeFormat'
import { roleLabel, type Account } from './accountStore'

const FIELD = 'min-h-11 rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

// "Sep 24, 2026, 2:18 PM" or "Sep 24, 2026, 14:18", in the reader's own time
// and the time format they chose.
function whenText(iso: string, format: TimeFormat): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: format === '12-hour' ? 'numeric' : '2-digit',
    minute: '2-digit',
    hourCycle: format === '12-hour' ? 'h12' : 'h23',
  })
}

/**
 * docs/product/personas.md's Administrator: "View system activity and audit
 * history." Audit records are readable and are never edited through the
 * application. Every event newest first, in pages, narrowed by the kind of
 * event, who did it, and when; each opens to the record before and after.
 */
export default function AdminAuditLogPage() {
  const { format } = useTimeFormat()
  const [known, setKnown] = useState<{ lookups: AuditLookups; accounts: Account[] } | null>(null)
  const [filters, setFilters] = useState<AuditFilters>(NO_FILTERS)
  const [page, setPage] = useState(0)
  const [log, setLog] = useState<AuditLog | null>(null)
  const [failed, setFailed] = useState(false)
  const [open, setOpen] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    getAuditLookups().then((loaded) => {
      if (!cancelled) setKnown(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!known) return
    let cancelled = false
    getAuditLog(filters, page, known.lookups).then((loaded) => {
      if (cancelled) return
      setFailed(loaded === null)
      setLog(loaded)
      setOpen(new Set())
    })
    return () => {
      cancelled = true
    }
  }, [known, filters, page])

  function change(next: Partial<AuditFilters>) {
    setFilters((current) => ({ ...current, ...next }))
    setPage(0)
  }

  function toggle(id: string) {
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const filtered = JSON.stringify(filters) !== JSON.stringify(NO_FILTERS)
  const first = page * AUDIT_PAGE_SIZE
  const total = log?.total ?? 0
  const lastPage = Math.max(0, Math.ceil(total / AUDIT_PAGE_SIZE) - 1)

  return (
    <AdminLayout current="audit-log" title="Audit log" subtitle="Who did what, and when">
      <form
        aria-label="Filter the audit log"
        className="flex flex-wrap items-end gap-3 rounded-surface border border-line bg-canvas p-3"
        onSubmit={(event) => event.preventDefault()}
      >
        <div>
          <label htmlFor="audit-category" className={LABEL}>
            Kind of event
          </label>
          <select id="audit-category" value={filters.category} onChange={(event) => change({ category: event.target.value })} className={`${FIELD} w-52`}>
            <option value="all">All events</option>
            {AUDIT_CATEGORIES.map((category) => (
              <option key={category.key} value={category.key}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="audit-actor" className={LABEL}>
            Done by
          </label>
          <select id="audit-actor" value={filters.actor} onChange={(event) => change({ actor: event.target.value })} className={`${FIELD} w-60`}>
            <option value="all">Everyone</option>
            <option value="system">The system</option>
            {(known?.accounts ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.firstName} {account.lastName} ({roleLabel(account.role)})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="audit-from" className={LABEL}>
            From
          </label>
          <input id="audit-from" type="date" max={filters.to || undefined} value={filters.from} onChange={(event) => change({ from: event.target.value })} className={FIELD} />
        </div>
        <div>
          <label htmlFor="audit-to" className={LABEL}>
            To
          </label>
          <input id="audit-to" type="date" min={filters.from || undefined} value={filters.to} onChange={(event) => change({ to: event.target.value })} className={FIELD} />
        </div>
        <button
          type="button"
          disabled={!filtered}
          onClick={() => {
            setFilters(NO_FILTERS)
            setPage(0)
          }}
          className="flex min-h-11 items-center justify-center rounded-control px-3 text-small font-bold text-ink-muted hover:bg-surface-2 disabled:opacity-40"
        >
          Clear filters
        </button>
      </form>

      {failed ? (
        <p role="alert" className="text-small font-semibold text-error">
          The audit log could not be read. Try again.
        </p>
      ) : !known || !log ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : log.entries.length === 0 ? (
        <EmptyState>{filtered ? 'No events match these filters.' : 'No events have been recorded yet.'}</EmptyState>
      ) : (
        <>
          <p role="status" className="text-small text-ink-muted">
            Showing {first + 1} to {first + log.entries.length} of {total} {total === 1 ? 'event' : 'events'}
          </p>
          <div className="overflow-x-auto rounded-surface border border-line">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['When', 'Done by', 'Event'].map((heading) => (
                    <th
                      key={heading}
                      scope="col"
                      className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide whitespace-nowrap text-ink-subtle uppercase"
                    >
                      {heading}
                    </th>
                  ))}
                  <th scope="col" className="border-b border-line bg-surface-2 py-2 pr-2 pl-3 text-right">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {log.entries.map((entry) => {
                  const expanded = open.has(entry.id)
                  const detailsId = `audit-details-${entry.id}`
                  return (
                    <Fragment key={entry.id}>
                      <tr>
                        <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted">
                          <time dateTime={entry.occurredAt}>{whenText(entry.occurredAt, format)}</time>
                        </td>
                        <td className="border-b border-line px-3 py-2 text-small">{entry.actorName}</td>
                        <th scope="row" className="border-b border-line px-3 py-2 text-left text-small font-normal">
                          <span className="block font-bold">{formatClockTimesInText(entry.title, format)}</span>
                          <span className="block text-caption text-ink-muted">{formatClockTimesInText(entry.detail, format)}</span>
                        </th>
                        <td className="border-b border-line py-2 pr-2 pl-3 text-right text-small">
                          <button
                            type="button"
                            aria-expanded={expanded}
                            aria-controls={detailsId}
                            aria-label={`${expanded ? 'Hide' : 'Show'} details of ${entry.title}`}
                            onClick={() => toggle(entry.id)}
                            className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold whitespace-nowrap hover:bg-surface-2"
                          >
                            {expanded ? 'Hide details' : 'Details'}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr id={detailsId}>
                          <td colSpan={4} className="border-b border-line bg-surface-2 px-3 py-3">
                            <AuditEventDetails entry={entry} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          <nav aria-label="Audit log pages" className="flex items-center justify-between gap-3">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-small text-ink-muted">
              Page {page + 1} of {lastPage + 1}
            </span>
            <button
              type="button"
              disabled={page >= lastPage}
              onClick={() => setPage((current) => Math.min(lastPage, current + 1))}
              className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:opacity-40"
            >
              Next
            </button>
          </nav>
        </>
      )}
    </AdminLayout>
  )
}
